import { ColletteButton, ColletteIconButton } from "@collette-travel/collette-common-controls";
import { Close } from "@mui/icons-material";
import { Stack, Typography, Box, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import ShareLinkModalSkeleton from "./share-link-modal-skeleton";

interface ShareLinkModalProps {
  handleClose: () => void;
}

const ShareLinkModal = ({ handleClose }: ShareLinkModalProps) => {
  const [currentUrl, setCurrentUrl] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentUrl(window.location.href);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleClose]);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to copy URL:", err);
    }
  };

  if (loading) {
    return <ShareLinkModalSkeleton />;
  }

  return (
    <>
      <style>
        {`
          .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border-width: 0;
          }
        `}
      </style>
      <Stack
        sx={{
          p: { xs: "1.6rem", sm: "3.2rem" },
          maxWidth: { xs: "36rem", sm: "50rem" },
          width: "100%",
          maxHeight: "22rem",
          gap: "3.6rem",
          borderRadius: "1rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",        
          backgroundColor: "background.default",
          border: "1px solid",
          borderColor: "grey.100"
        }}
        data-testid="share-link-modal-root"
      >
      <Stack gap="0.8rem" width="100%">
        <Typography variant="h4">
          Share this tour
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "text.secondary", fontWeight: 500 }}>
          Share tour info with your friends
        </Typography>
      </Stack>

      <Box sx={{ 
          display: "flex", 
          gap: "0.8rem", 
          alignItems: "center",
          justifyContent: "space-between",
          border: "1px solid var(--theme-palette-grey-200)",
          borderRadius: "0.4rem", 
          height: "56px", 
          width: "100%",
          padding: "1rem 1.4rem"
          }}
        data-testid="share-link-modal-content"
        >
        <TextField
          fullWidth
          value={currentUrl}
          variant="outlined"
          slotProps={{ input: { readOnly: true } }}
          sx={{
            "& .MuiOutlinedInput-input": {
              padding: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.secondary"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none"
            }
          }}
          data-testid="share-link-input"
        />
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {copied ? "Link copied to clipboard" : ""}
        </div>
        <ColletteButton
          label={copied ? "Copied!" : "Copy Link"}
          variant="outlined"
          onClick={handleCopyToClipboard}
          testId="share-link-copy-button"
          sx={{
            textWrap: "nowrap",
            backgroundColor: copied ? "background.cool_strong" : "background.default"
          }}
        />
      </Box>

      <ColletteIconButton
        testId="share-link-modal-close-button"
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: "1.6rem",
          right: "1.6rem"
        }}
      >
        <Close />
      </ColletteIconButton>
    </Stack>
    </>
  );
};

export default ShareLinkModal;
